'use client';

import { type RefObject, useEffect, useRef, useState } from 'react';

import {
  overlayRegistry,
  registerOverlay,
  unregisterOverlay,
} from '@/app/components/overlays/core/overlayRegistry';
import type {
  OverlayEntry,
  OverlayKey,
  OverlayRegistrationProps,
} from '@/app/components/overlays/core/types';
import { getOverlayRegistryKey } from '@/app/components/overlays/utils/getOverlayRegistryKey';

/**
 * Return type for the `useOverlay` hook.
 */
export interface UseOverlayReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  overlayProps: {
    isOpen: boolean;
    onClose: () => void;
    overlayKey: OverlayKey;
    instanceId?: string;
    ignoreClickTargetRef?: RefObject<EventTarget | null>;
  };
}

export const useOverlay = (
  options: OverlayRegistrationProps
): UseOverlayReturn => {
  const { overlayKey, instanceId } = options;
  const registryKey = getOverlayRegistryKey({ overlayKey, instanceId });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const entryStateRef = useRef<boolean>(false);
  const lastOpenTargetRef = useRef<EventTarget | null>(null);

  // Keep ref in sync with current open state
  useEffect(() => {
    entryStateRef.current = isOpen;
  }, [isOpen]);

  // Register the overlay in global registry on mount
  useEffect(() => {
    const entry: OverlayEntry = {
      setOpen: (value: boolean) => {
        entryStateRef.current = value;
        setIsOpen(value);
      },
      isOpen: () => entryStateRef.current,
    };

    registerOverlay({ overlayKey, instanceId }, entry);

    // Handle queued open/toggle
    if (overlayRegistry.openQueue.has(registryKey)) {
      overlayRegistry.openQueue.delete(registryKey);
      open();
    } else if (overlayRegistry.toggleQueue.has(registryKey)) {
      overlayRegistry.toggleQueue.delete(registryKey);
      toggle();
    }

    return () => {
      unregisterOverlay({ overlayKey, instanceId });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryKey]);

  function open() {
    if (entryStateRef.current) return;
    lastOpenTargetRef.current = window.event?.target ?? null;
    setIsOpen(true);
  }

  function close() {
    if (!entryStateRef.current) return;
    setIsOpen(false);
  }

  function toggle() {
    entryStateRef.current ? close() : open();
  }

  return {
    isOpen,
    open,
    close,
    toggle,
    overlayProps: {
      isOpen,
      onClose: close,
      overlayKey,
      instanceId,
      ignoreClickTargetRef: lastOpenTargetRef,
    },
  };
};
